import React from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import { Grid2 } from '@mui/material';
import { formatDateDMY } from '../../../utils/dateUtils';

// SVG Icon components
const InfoIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="21" height="20" viewBox="0 0 21 20" fill="none" {...props}>
    <path d="M10.431 18.3346C15.0143 18.3346 18.7643 14.5846 18.7643 10.0013C18.7643 5.41797 15.0143 1.66797 10.431 1.66797C5.84766 1.66797 2.09766 5.41797 2.09766 10.0013C2.09766 14.5846 5.84766 18.3346 10.431 18.3346Z" stroke="#5A8AEA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10.4297 6.66797V10.8346" stroke="#5A8AEA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10.4258 13.332H10.4333" stroke="#5A8AEA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const EyeIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <path d="M15.5799 12.0019C15.5799 13.9819 13.9799 15.5819 11.9999 15.5819C10.0199 15.5819 8.41992 13.9819 8.41992 12.0019C8.41992 10.0219 10.0199 8.42188 11.9999 8.42188C13.9799 8.42188 15.5799 10.0219 15.5799 12.0019Z" stroke="#5A8AEA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M11.9998 20.2688C15.5298 20.2688 18.8198 18.1887 21.1098 14.5887C22.0098 13.1787 22.0098 10.8087 21.1098 9.39875C18.8198 5.79875 15.5298 3.71875 11.9998 3.71875C8.46984 3.71875 5.17984 5.79875 2.88984 9.39875C1.98984 10.8087 1.98984 13.1787 2.88984 14.5887C5.17984 18.1887 8.46984 20.2688 11.9998 20.2688Z" stroke="#5A8AEA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const PrinterIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...props}>
    <path d="M6.04102 5.83464H13.9577V4.16797C13.9577 2.5013 13.3327 1.66797 11.4577 1.66797H8.54102C6.66602 1.66797 6.04102 2.5013 6.04102 4.16797V5.83464Z" stroke="#1C1C1C" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13.3327 12.5V15.8333C13.3327 17.5 12.4993 18.3333 10.8327 18.3333H9.16602C7.49935 18.3333 6.66602 17.5 6.66602 15.8333V12.5H13.3327Z" stroke="#1C1C1C" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M17.5 8.33203V12.4987C17.5 14.1654 16.6667 14.9987 15 14.9987H13.3333V12.4987H6.66667V14.9987H5C3.33333 14.9987 2.5 14.1654 2.5 12.4987V8.33203C2.5 6.66536 3.33333 5.83203 5 5.83203H15C16.6667 5.83203 17.5 6.66536 17.5 8.33203Z" stroke="black" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14.1673 12.5H13.159H5.83398" stroke="black" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5.83398 9.16797H8.33398" stroke="black" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const DotsIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" {...props}>
    <path d="M3.75 7.5C2.925 7.5 2.25 8.175 2.25 9C2.25 9.825 2.925 10.5 3.75 10.5C4.575 10.5 5.25 9.825 5.25 9C5.25 8.175 4.575 7.5 3.75 7.5Z" fill="#101828"/>
    <path d="M14.25 7.5C13.425 7.5 12.75 8.175 12.75 9C12.75 9.825 13.425 10.5 14.25 10.5C15.075 10.5 15.75 9.825 15.75 9C15.75 8.175 15.075 7.5 14.25 7.5Z" fill="#101828"/>
    <path d="M9 7.5C8.175 7.5 7.5 8.175 7.5 9C7.5 9.825 8.175 10.5 9 10.5C9.825 10.5 10.5 9.825 10.5 9C10.5 8.175 9.825 7.5 9 7.5Z" fill="#101828"/>
  </svg>
);

export interface InvoiceMobileCardProps {
  row: {
    date: string;
    patientName: string;
    patientInfo: string;
    status: string;
    billNo: string;
    clinic?: string | null;
    billType: string;
    provider: string;
    totalAmt: string;
    concessionAmt: string;
    netAmt: string;
    paidAmt: string;
    balanceAmt: string;
    billRemark: string | null;
  };
}

const InvoiceMobileCard: React.FC<InvoiceMobileCardProps> = ({ row }) => {
  const { t: tBilling } = useTranslation('billing_config');

  return (
    <Paper 
      elevation={3} 
      sx={{ p: 2, borderRadius: 3, mb: 2, background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ef 100%)' }}
      data-testid={`invoice-mobile-card-${row.billNo || 'unknown'}`}
      role="article"
      aria-label={tBilling('invoice.mobile_card.aria.invoice_label', { billNo: row.billNo, patientName: row.patientName })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{row.patientName || '-'}</Typography>
          <Typography variant="body2" color="text.secondary">{row.patientInfo || '-'}</Typography>
        </Box>
        <InvoiceStatusBadge status={row.status} />
      </Box>
      <Box sx={{ my: 1, borderBottom: '1px solid #e0e0e0' }} />
      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 6, sm: 4 }}>
          <Typography variant="caption" color="text.secondary">{tBilling('invoice.mobile_card.date')}</Typography>
          <Typography variant="body2">{row.date ? formatDateDMY(row.date) : '-'}</Typography>
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 4 }}>
          <Typography variant="caption" color="text.secondary">{tBilling('invoice.mobile_card.bill_no')}</Typography>
          <Typography variant="body2">{row.billNo || '-'}</Typography>
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 4 }}>
          <Typography variant="caption" color="text.secondary">{tBilling('invoice.mobile_card.clinic')}</Typography>
          <Typography variant="body2">{row.clinic || '-'}</Typography>
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 4 }}>
          <Typography variant="caption" color="text.secondary">{tBilling('invoice.mobile_card.bill_type')}</Typography>
          <Typography variant="body2">{row.billType || '-'}</Typography>
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 4 }}>
          <Typography variant="caption" color="text.secondary">{tBilling('invoice.mobile_card.provider')}</Typography>
          <Typography variant="body2">{row.provider || '-'}</Typography>
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 4 }}>
          <Typography variant="caption" color="text.secondary">{tBilling('invoice.mobile_card.total_amt')}</Typography>
          <Typography variant="body2">{row.totalAmt || '-'}</Typography>
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 4 }}>
          <Typography variant="caption" color="text.secondary">{tBilling('invoice.mobile_card.concession_amt')}</Typography>
          <Typography variant="body2">{row.concessionAmt || '-'}</Typography>
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 4 }}>
          <Typography variant="caption" color="text.secondary">{tBilling('invoice.mobile_card.net_amt')}</Typography>
          <Typography variant="body2">{row.netAmt || '-'}</Typography>
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 4 }}>
          <Typography variant="caption" color="text.secondary">{tBilling('invoice.mobile_card.paid_amt')}</Typography>
          <Typography variant="body2">{row.paidAmt || '-'}</Typography>
        </Grid2>
        <Grid2 size={{ xs: 6, sm: 4 }}>
          <Typography variant="caption" color="text.secondary">{tBilling('invoice.mobile_card.balance_amt')}</Typography>
          <Typography variant="body2">{row.balanceAmt || '-'}</Typography>
        </Grid2>
      </Grid2>
      <Box sx={{ my: 1, borderBottom: '1px solid #e0e0e0' }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <InfoIcon style={{ verticalAlign: 'middle', marginRight: 4 }} />
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>{row.billRemark || '-'}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
        <IconButton size="small" aria-label={tBilling('invoice.mobile_card.aria.view_invoice', { billNo: row.billNo })} data-testid={`invoice-mobile-card-view-${row.billNo}`} tabIndex={0}><EyeIcon /></IconButton>
        <IconButton size="small" aria-label={tBilling('invoice.mobile_card.aria.print_invoice', { billNo: row.billNo })} data-testid={`invoice-mobile-card-print-${row.billNo}`} tabIndex={0}><PrinterIcon /></IconButton>
        <IconButton size="small" aria-label={tBilling('invoice.mobile_card.aria.more_actions', { billNo: row.billNo })} data-testid={`invoice-mobile-card-more-${row.billNo}`} tabIndex={0}><DotsIcon /></IconButton>
      </Box>
    </Paper>
  );
};

export default InvoiceMobileCard;
